/**
 * DM-13 round 2 — `/docs/$role` — карта роли (Calm Industrial redesign).
 *
 * Pre-round-2 проблема: тройная дубликация навигации (global sidebar + docs
 * sidebar + main bullet list). User feedback «выглядит уебански».
 *
 * Round 2: hideSidebar на entry, 3 Hero Cards (onboarding/first read/quick
 * action) + категоризированные section cards. Inter (не PT Serif) для h1.
 */

import * as m from '@staiter/i18n';
import type { DocPageSummary } from '@staiter/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { DocsEmpty, DocsLayout, DocsSkeleton } from '~/components/docs';
import { fetchDocsToc } from '~/lib/api/docs';
import styles from './_auth.docs.$role.index.module.css';

export const Route = createFileRoute('/_auth/docs/$role/')({
  component: DocsRolePage,
});

function DocsRolePage(): JSX.Element {
  const params = useParams({ from: '/_auth/docs/$role/' });

  const { data, isLoading } = useQuery({
    queryKey: ['docs', 'toc', params.role, 'ru'],
    queryFn: () => fetchDocsToc({ role: params.role, locale: 'ru' }),
  });

  const allPages: DocPageSummary[] = data?.sections.flatMap((s) => s.pages) ?? [];
  const totalPages = allPages.length;

  const role = params.role;
  const heroCards = pickHeroCards(allPages);
  const categorized = categorize(allPages, heroCards);

  return (
    <DocsLayout
      toc={data}
      currentRole={params.role}
      currentSlug={null}
      hideSidebar
      breadcrumbs={[{ label: 'Документация', href: '/docs' }, { label: roleLabel(role) }]}
    >
      {isLoading ? (
        <DocsSkeleton />
      ) : totalPages === 0 ? (
        <DocsEmpty />
      ) : (
        <div className={styles.page}>
          <header className={styles.heroHeader}>
            <h1 className={styles.title}>Документация — {roleLabel(role)}</h1>
            <p className={styles.lede}>{roleLede(role)}</p>
          </header>

          {heroCards.length > 0 ? (
            <div className={styles.heroGrid}>
              {heroCards.map((card) => (
                <a
                  key={card.page.id}
                  className={styles.heroCard}
                  href={`/docs/${card.page.role}/${card.page.slug}`}
                >
                  <span className={styles.heroCardKicker}>{card.kicker}</span>
                  <h3 className={styles.heroCardTitle}>{card.page.title}</h3>
                  <p className={styles.heroCardDesc}>{card.desc}</p>
                  <span className={styles.heroCardArrow}>{m.ui_otkryt_2()}</span>
                </a>
              ))}
            </div>
          ) : null}

          {categorized.map((cat) =>
            cat.pages.length > 0 ? (
              <section key={cat.title} className={styles.sectionGroup}>
                <h2 className={styles.sectionTitle}>{cat.title}</h2>
                <div className={styles.sectionGrid}>
                  {cat.pages.map((page) => (
                    <a
                      key={page.id}
                      className={styles.sectionCard}
                      href={`/docs/${page.role}/${page.slug}`}
                    >
                      {page.title}
                    </a>
                  ))}
                </div>
              </section>
            ) : null,
          )}
        </div>
      )}
    </DocsLayout>
  );
}

function roleLabel(role: string): string {
  const map: Record<string, string> = {
    manager: 'Руководитель',
    accountant: 'Бухгалтер',
    reviewer: 'Старший бухгалтер',
    admin: 'Администратор',
    viewer: 'Аудитор',
    warehouse: 'Кладовщик',
    shared: 'Общие материалы',
  };
  return map[role] ?? role;
}

function roleLede(role: string): string {
  const map: Record<string, string> = {
    manager: 'Утренний обзор, согласования, контроль показателей и закрытие периода.',
    accountant: 'Конфликты, движения по складу, инвентаризация, справочники и работа агента.',
    reviewer: 'Очередь согласований уровней 2 и 3, второй подпись, журнал действий.',
    admin: 'Подключения, доступы, обслуживание агента и фоновых задач.',
    viewer: 'Доказательная база, журнал действий, квартальный аудит, контроль целостности.',
    warehouse: 'Мастер ввода движений, инвентаризация, партии, частые ситуации.',
    shared: 'Общие разделы: вход, навигация, темы, горячие клавиши.',
  };
  return map[role] ?? 'Разделы документации.';
}

interface HeroCard {
  page: DocPageSummary;
  kicker: string;
  desc: string;
}

/** Топ-3 entry points для роли: онбординг + первая полезная задача + reference. */
function pickHeroCards(pages: DocPageSummary[]): HeroCard[] {
  const cards: HeroCard[] = [];

  const onboarding = pages.find((p) => /00-onboarding|^readme$/i.test(p.slug));
  if (onboarding) {
    cards.push({
      page: onboarding,
      kicker: 'Начать здесь',
      desc: 'Первые шаги в системе — что увидеть, проверить и попробовать в первый день.',
    });
  }

  // «Первая полезная задача» — первая страница после онбординга с числовым префиксом.
  const firstTask = pages.find(
    (p) =>
      /^\d{2}-/.test(p.slug) &&
      p.slug !== onboarding?.slug &&
      !/troubleshooting|faq|readme/i.test(p.slug),
  );
  if (firstTask) {
    cards.push({
      page: firstTask,
      kicker: 'Дальше',
      desc: 'Ключевой ежедневный сценарий роли — на чём вы будете работать постоянно.',
    });
  }

  // FAQ как reference (если есть).
  const faq = pages.find((p) => /faq/i.test(p.slug));
  if (faq) {
    cards.push({
      page: faq,
      kicker: 'Справочник',
      desc: 'Частые вопросы и короткие ответы — посмотрите, прежде чем спрашивать.',
    });
  }

  return cards.slice(0, 3);
}

interface Category {
  title: string;
  pages: DocPageSummary[];
}

function categorize(allPages: DocPageSummary[], hero: HeroCard[]): Category[] {
  const heroIds = new Set(hero.map((c) => c.page.id));
  const rest = allPages.filter((p) => !heroIds.has(p.id));

  const buckets: Record<string, DocPageSummary[]> = {
    'Подключения и интеграции': [],
    'Доступы и роли': [],
    Согласования: [],
    'Ежедневная работа': [],
    'Обслуживание и журналы': [],
    'Решение проблем': [],
  };

  for (const p of rest) {
    const s = p.slug.toLowerCase();
    if (/integration|integration|bitrix|adesk|1c/.test(s) || s.includes('integrations')) {
      buckets['Подключения и интеграции']?.push(p);
    } else if (/rbac|warehouse-access|users|permissions|invite/.test(s)) {
      buckets['Доступы и роли']?.push(p);
    } else if (/approval|tier|sign|sla/.test(s)) {
      buckets['Согласования']?.push(p);
    } else if (/audit|memory-governance|jobs|monitor|hash/.test(s)) {
      buckets['Обслуживание и журналы']?.push(p);
    } else if (/troubleshoot|nesh|incident|faq/.test(s)) {
      buckets['Решение проблем']?.push(p);
    } else {
      buckets['Ежедневная работа']?.push(p);
    }
  }

  return Object.entries(buckets).map(([title, pages]) => ({ title, pages }));
}
